import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded'
import CreditCardIcon from '@mui/icons-material/CreditCard'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import Typography from '@mui/joy/Typography'
import { useTranslation } from 'react-i18next'

import BenefitCodePayment from '@/components/checkout/benefit-code-payment'
import CheckoutContactInfo from '@/components/checkout/contact-info'
import CheckoutDisclaimer from '@/components/checkout/disclaimer'
import DiscountCode from '@/components/checkout/discount-code'
import CheckoutOrderSummaryDesktop from '@/components/checkout/order-summary/desktop'
import CheckoutOrderSummaryMobile from '@/components/checkout/order-summary/mobile'
import { formatCzkAmount } from '@/components/checkout/order-summary/utils'
import ReservationCancellationAlert from '../reservation-cancellation-alert'
import ReservationExtensionAlert from '../reservation-extension-alert'
import CheckoutSendSmsPreference from '@/components/checkout/send-sms-preference'
import CheckoutVehicleInfo from '@/components/checkout/vehicle-info'
import type { CheckoutFormController } from './controller'
import type { CheckoutViewModel } from './view-model'

const BOTTOM_BAR_MIN_HEIGHT = 70

const BACK_BUTTON_SX = {
    color: 'black',
    '&& svg': {
        fill: 'currentColor',
    },
    '&:hover': {
        color: '#484848',
    },
    '&&:hover svg': {
        fill: 'currentColor',
    },
} as const

const PAY_BUTTON_SX = {
    fontSize: '15px',
    padding: '0.875rem 1.25rem',
    borderRadius: '7px',
    backgroundColor: 'customBlack.main',
} as const

function StripePaymentAction({
    controller,
    paymentCtaLabel,
    disabled,
}: {
    controller: CheckoutFormController
    paymentCtaLabel: string
    disabled: boolean
}) {
    return (
        // Required by CheckoutContactInfoForm's email blur/submission contract.
        <Button
            data-checkout-submission
            color="primary"
            variant="solid"
            size="sm"
            endDecorator={<CreditCardIcon />}
            onClick={() => {
                void controller.pay.stripe.submit()
            }}
            disabled={disabled}
            loading={controller.pay.stripe.requestInProgress}
            sx={PAY_BUTTON_SX}
        >
            {paymentCtaLabel}
        </Button>
    )
}

type CheckoutContentProps = {
    viewModel: CheckoutViewModel
    controller: CheckoutFormController
    benefitCodePaymentExpanded: boolean
    onBenefitCodePaymentExpandedChange: (expanded: boolean) => void
    onBack?: () => void
}

export function CheckoutDesktopContent({
    viewModel,
    controller,
    benefitCodePaymentExpanded,
    onBenefitCodePaymentExpandedChange,
    onBack,
}: CheckoutContentProps) {
    const { t, i18n } = useTranslation()
    const { form } = controller
    const locale = i18n.resolvedLanguage || i18n.language
    const paymentCtaLabel = `${t('checkout.actions.toPaymentCTA')} ${formatCzkAmount(viewModel.summaryProps.finalPrice, locale)}`

    return (
        <Box
            sx={{
                backgroundColor: 'white',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                flexGrow: 1,
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    maxWidth: '1080px',
                    mx: 'auto',
                    px: 2,
                    pt: 5,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: 2,
                }}
            >
                <Button
                    variant="plain"
                    startDecorator={<ArrowBackIosNewRoundedIcon />}
                    onClick={onBack}
                    sx={BACK_BUTTON_SX}
                >
                    {t('checkout.actions.back')}
                </Button>
            </Box>
            <Box
                sx={{
                    width: '100%',
                    maxWidth: '1080px',
                    mx: 'auto',
                    px: 2,
                    pb: 4,
                    pt: 2,
                    display: 'grid',
                    gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
                    gap: 4,
                    alignItems: 'start',
                }}
            >
                <Box
                    sx={{
                        gridColumn: 'span 7',
                        minWidth: 0,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2.5,
                        }}
                    >
                        <CheckoutContactInfo
                            desktop
                            mode={viewModel.checkoutMode}
                            control={form.control}
                            setValue={form.setValue}
                            summaryValues={controller.contact.summaryValues}
                            invoiceRequired={viewModel.invoiceRequired}
                            shouldEdit={controller.contact.shouldEdit}
                            defaultPhoneCountry={controller.contact.defaultPhoneCountry}
                            hideActions={viewModel.checkoutMode === 'single_submit'}
                            requestInProgress={controller.contact.requestInProgress}
                            errorMessage={controller.contact.errorMessage}
                            emailDomainSuggestion={controller.contact.emailDomainSuggestion}
                            sectionRef={controller.contact.sectionRef}
                            onEdit={controller.contact.openForEdit}
                            onCancel={controller.contact.cancelEdit}
                            onClear={controller.contact.clear}
                            onPhoneCountryChange={controller.contact.selectPhoneCountry}
                            onCheckEmailDomainSuggestion={controller.contact.checkEmailDomainSuggestion}
                            onClearEmailDomainSuggestion={controller.contact.clearEmailDomainSuggestion}
                            onSave={controller.contact.save}
                        />
                        <CheckoutVehicleInfo
                            desktop
                            mode={viewModel.checkoutMode}
                            control={form.control}
                            setValue={form.setValue}
                            clearErrors={form.clearErrors}
                            summaryValues={controller.vehicle.summaryValues}
                            currentUserCars={viewModel.currentUserCars}
                            shouldEdit={controller.vehicle.shouldEdit}
                            missingPlateDismissed={controller.vehicle.missingPlateDismissed}
                            hideActions={viewModel.checkoutMode === 'single_submit'}
                            requestInProgress={controller.vehicle.requestInProgress}
                            errorMessage={controller.vehicle.errorMessage}
                            sectionRef={controller.vehicle.sectionRef}
                            onEdit={controller.vehicle.openForEdit}
                            onCancel={controller.vehicle.cancelEdit}
                            onClear={controller.vehicle.clear}
                            onSave={controller.vehicle.save}
                        />
                        <DiscountCode currentlyAppliedCoupon={viewModel.currentlyAppliedCoupon} />
                        <BenefitCodePayment
                            availableBenefitCodes={viewModel.availableBenefitCodes}
                            expanded={benefitCodePaymentExpanded}
                            onExpandedChange={onBenefitCodePaymentExpandedChange}
                            requestInProgress={controller.pay.benefitCode.requestInProgress}
                            errorMessage={controller.pay.benefitCode.errorMessage}
                            onSubmit={controller.pay.benefitCode.submit}
                        />
                        <CheckoutSendSmsPreference
                            sendSms={viewModel.sendSms}
                            alreadyKnowGarage={viewModel.alreadyKnowGarage}
                        />
                        <CheckoutDisclaimer
                            links={viewModel.disclaimerLinks}
                            control={form.control}
                            clearErrors={form.clearErrors}
                            sectionRef={controller.disclaimer.sectionRef}
                        />
                        {controller.pay.stripe.errorMessage ? (
                            <Typography level="body-sm" color="danger">
                                {controller.pay.stripe.errorMessage}
                            </Typography>
                        ) : null}
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                        }}>
                            <StripePaymentAction
                                controller={controller}
                                paymentCtaLabel={paymentCtaLabel}
                                disabled={benefitCodePaymentExpanded}
                            />
                        </Box>
                    </Box>
                </Box>
                <Box
                    sx={{
                        gridColumn: 'span 5',
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2.5,
                    }}
                >
                    <CheckoutOrderSummaryDesktop {...viewModel.summaryProps} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {viewModel.summaryProps.minimumStay ? (
                            <ReservationExtensionAlert parkingPeriod={viewModel.summaryProps.minimumStay.parkingPeriod} />
                        ) : null}
                        <ReservationCancellationAlert />
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}

export function CheckoutMobileContent({
    viewModel,
    controller,
    benefitCodePaymentExpanded,
    onBenefitCodePaymentExpandedChange,
    isPwa,
    onBack,
}: CheckoutContentProps & {
    isPwa?: boolean
}) {
    const { t, i18n } = useTranslation()
    const { form } = controller
    const locale = i18n.resolvedLanguage || i18n.language
    const paymentCtaLabel = `${t('checkout.actions.toPaymentCTA')} ${formatCzkAmount(viewModel.summaryProps.finalPrice, locale)}`

    return (
        <Box
            sx={{
                backgroundColor: '#F2F2F2',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                flexGrow: 1,
                minHeight: isPwa ? '100vh' : undefined,
            }}
        >
            <CheckoutOrderSummaryMobile {...viewModel.summaryProps} />
            <Box sx={{
                px: 1,
                pb: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
            }}>
                {viewModel.summaryProps.minimumStay ? (
                    <ReservationExtensionAlert parkingPeriod={viewModel.summaryProps.minimumStay.parkingPeriod} mobile />
                ) : null}
                <ReservationCancellationAlert mobile />
            </Box>
            <Box
                sx={{
                    px: 1,
                    py: 1.5,
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 10,
                    background: 'white',
                    flexGrow: 1,
                    gap: 2.5,
                }}
            >
                <CheckoutContactInfo
                    mode={viewModel.checkoutMode}
                    control={form.control}
                    setValue={form.setValue}
                    summaryValues={controller.contact.summaryValues}
                    invoiceRequired={viewModel.invoiceRequired}
                    shouldEdit={controller.contact.shouldEdit}
                    defaultPhoneCountry={controller.contact.defaultPhoneCountry}
                    hideActions={viewModel.checkoutMode === 'single_submit'}
                    requestInProgress={controller.contact.requestInProgress}
                    errorMessage={controller.contact.errorMessage}
                    emailDomainSuggestion={controller.contact.emailDomainSuggestion}
                    sectionRef={controller.contact.sectionRef}
                    onEdit={controller.contact.openForEdit}
                    onCancel={controller.contact.cancelEdit}
                    onClear={controller.contact.clear}
                    onPhoneCountryChange={controller.contact.selectPhoneCountry}
                    onCheckEmailDomainSuggestion={controller.contact.checkEmailDomainSuggestion}
                    onClearEmailDomainSuggestion={controller.contact.clearEmailDomainSuggestion}
                    onSave={controller.contact.save}
                />
                <CheckoutVehicleInfo
                    mode={viewModel.checkoutMode}
                    control={form.control}
                    setValue={form.setValue}
                    clearErrors={form.clearErrors}
                    summaryValues={controller.vehicle.summaryValues}
                    currentUserCars={viewModel.currentUserCars}
                    shouldEdit={controller.vehicle.shouldEdit}
                    missingPlateDismissed={controller.vehicle.missingPlateDismissed}
                    hideActions={viewModel.checkoutMode === 'single_submit'}
                    requestInProgress={controller.vehicle.requestInProgress}
                    errorMessage={controller.vehicle.errorMessage}
                    sectionRef={controller.vehicle.sectionRef}
                    onEdit={controller.vehicle.openForEdit}
                    onCancel={controller.vehicle.cancelEdit}
                    onClear={controller.vehicle.clear}
                    onSave={controller.vehicle.save}
                />
                <DiscountCode currentlyAppliedCoupon={viewModel.currentlyAppliedCoupon} />
                <BenefitCodePayment
                    availableBenefitCodes={viewModel.availableBenefitCodes}
                    expanded={benefitCodePaymentExpanded}
                    onExpandedChange={onBenefitCodePaymentExpandedChange}
                    requestInProgress={controller.pay.benefitCode.requestInProgress}
                    errorMessage={controller.pay.benefitCode.errorMessage}
                    onSubmit={controller.pay.benefitCode.submit}
                />
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1,
                    justifyContent: 'flex-end',
                    gap: 2.5,
                }}>
                    <CheckoutSendSmsPreference
                        sendSms={viewModel.sendSms}
                        alreadyKnowGarage={viewModel.alreadyKnowGarage}
                        isPwa={isPwa}
                    />
                    <CheckoutDisclaimer
                        links={viewModel.disclaimerLinks}
                        control={form.control}
                        clearErrors={form.clearErrors}
                        sectionRef={controller.disclaimer.sectionRef}
                    />
                    {controller.pay.stripe.errorMessage ? (
                        <Typography level="body-sm" color="danger">
                            {controller.pay.stripe.errorMessage}
                        </Typography>
                    ) : null}
                    <Box minHeight={BOTTOM_BAR_MIN_HEIGHT} />
                </Box>
            </Box>
            <Box
                sx={{
                    position: 'fixed',
                    width: '100vw',
                    minHeight: BOTTOM_BAR_MIN_HEIGHT,
                    bottom: 0,
                    left: 0,
                    zIndex: 10,
                    backgroundColor: 'white',
                    p: '10px 7px 22px',
                    borderTopWidth: '1px',
                    borderTopStyle: 'solid',
                    borderTopColor: 'var(--joy-palette-primary-300)',
                    display: 'flex',
                    justifyContent: 'space-between',
                }}
            >
                <Button
                    variant="plain"
                    startDecorator={<ArrowBackIosNewRoundedIcon />}
                    onClick={onBack}
                    sx={BACK_BUTTON_SX}
                >
                    {t('checkout.actions.back')}
                </Button>
                <StripePaymentAction
                    controller={controller}
                    paymentCtaLabel={paymentCtaLabel}
                    disabled={benefitCodePaymentExpanded}
                />
            </Box>
        </Box>
    )
}
